import { useRef } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactCard from '../components/ContactCard';

const socialWins = [
  {
    metric: '5.6M',
    label: 'monthly impressions',
    copy: 'Generated for a national fitness franchise with short-form video and UGC campaigns.',
  },
  {
    metric: '3x',
    label: 'DM inquiries',
    copy: 'Increase in inbound conversations once we implemented community management and social funnels.',
  },
  {
    metric: '220%',
    label: 'follower growth',
    copy: 'Achieved in 4 months for a B2B brand by repurposing long-form content into LinkedIn carousels and clips.',
  },
];

const socialPlaybook = [
  {
    title: 'Plan',
    text: 'Monthly content calendars anchored to campaigns, product launches, and cultural moments.',
  },
  {
    title: 'Produce',
    text: 'In-house designers, videographers, and copywriters craft content tailored to each platform.',
  },
  {
    title: 'Publish',
    text: 'Scheduling, hashtag research, community management, and proactive engagement keep momentum high.',
  },
  {
    title: 'Promote',
    text: 'Smart boosting, retargeting, and partnership strategies turn attention into clicks, sign-ups, and sales.',
  },
];

export default function SocialMediaLanding() {
  const contactRef = useRef(null);
  const handleTalkClick = () => {
    contactRef.current?.focusForm?.();
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-white via-sky-50 to-indigo-100 font-sans text-slate-900">
      <Navbar onTalkClick={handleTalkClick} />

      <main className="flex-1 pt-32">
        <section id="services" className="mx-auto max-w-6xl px-6">
          <div className="grid items-center gap-10 md:grid-cols-2">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-slate-500">Social media, fully managed</p>
              <h1 className="mt-4 text-4xl font-semibold md:text-5xl">Stay top-of-mind with scroll-stopping social</h1>
              <p className="mt-4 text-lg text-slate-600">
                We build your social presence from strategy to execution — creating content that grows your community,
                sparks conversations, and drives conversions.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={handleTalkClick}
                  className="inline-flex items-center justify-center rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white shadow hover:opacity-90"
                >
                  Book a social growth call →
                </button>
                <a
                  href="tel:14374944736"
                  className="inline-flex items-center justify-center rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-800 transition hover:bg-white"
                >
                  Talk now: 1‑437‑494‑4736
                </a>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              {socialWins.map((win) => (
                <div key={win.label} className="rounded-3xl border border-slate-200 bg-white/70 p-5 text-center shadow-sm backdrop-blur">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{win.label}</p>
                  <p className="mt-3 text-3xl font-semibold text-slate-900">{win.metric}</p>
                  <p className="mt-2 text-xs text-slate-500">{win.copy}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto mt-16 max-w-6xl px-6">
          <div className="rounded-3xl border border-blue-100 bg-white/80 p-10 shadow-sm backdrop-blur">
            <h2 className="text-2xl font-semibold text-slate-900">What we manage for you</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 md:grid-cols-3">
              {['Strategy & positioning', 'Content production', 'Copywriting & captions', 'Publishing & scheduling', 'Community engagement', 'Social ads & retargeting'].map((item) => (
                <div key={item} className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm">
                  {item}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="work" className="mx-auto mt-16 max-w-6xl px-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
              <p className="text-sm uppercase tracking-[0.2em] text-slate-500">Client spotlight</p>
              <h2 className="mt-2 text-3xl font-semibold text-slate-900">Launching TikTok for a professional services brand</h2>
              <p className="mt-4 text-sm text-slate-600">
                We scripted, filmed, and edited short-form content while handling replies and collaborations. Within 90 days,
                the account grew to 42K followers and delivered steady inbound leads.
              </p>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
              <p className="text-sm uppercase tracking-[0.2em] text-slate-500">Community playbook</p>
              <p className="mt-4 text-sm text-slate-600">
                Weekly live sessions, surprise-and-delight giveaways, and personalised DMs helped a SaaS brand reduce churn
                by 18% and increase referrals — all powered by our team.
              </p>
            </div>
          </div>
        </section>

        <section id="process" className="mx-auto mt-16 max-w-6xl px-6">
          <div className="rounded-3xl border border-slate-200 bg-white/80 p-10 shadow-sm backdrop-blur">
            <h2 className="text-3xl font-semibold text-slate-900">Our social growth loop</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-4">
              {socialPlaybook.map((item) => (
                <div key={item.title} className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-500">{item.title}</h3>
                  <p className="mt-3 text-sm text-slate-600">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto mt-16 max-w-4xl px-6 text-center">
          <div className="rounded-3xl border border-blue-100 bg-white/80 p-10 shadow-sm backdrop-blur">
            <h2 className="text-3xl font-semibold text-slate-900 md:text-4xl">Ready to lead the conversation?</h2>
            <p className="mt-3 text-lg text-slate-600">
              Let’s audit your social presence and map a 90-day plan to turn attention into customers.
            </p>
            <button
              type="button"
              onClick={handleTalkClick}
              className="mt-6 mb-2 inline-flex items-center justify-center rounded-full bg-slate-900 px-6 py-3 text-white shadow hover:opacity-90"
            >
              Claim my social strategy →
            </button>
          </div>
        </section>
      </main>

      <ContactCard ref={contactRef} className="mt-16" />
      <Footer />
    </div>
  );
}
